import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../apollo-client';
import NextNprogress from 'nextjs-progressbar';

import Layout from '../components/Layout';
import { Container, Box, Heading } from '@chakra-ui/react';

import metaRobots from '../helpers/metaRobots';
import sitePath from '../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import sharecardImage from '../public/images/sharecard.jpg';
import styles from '../styles/legal/content.module.scss';

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
};

const GET_APPLICANTPRIVACY = gql`
  query GetApplicantPrivacy {
    page(id: "job-applicant-privacy", idType: URI) {
      title
      seo {
        title
        metaDesc
        metaRobotsNofollow
        metaRobotsNoindex
      }
      content
    }
  }
`;

export async function getServerSideProps({ locale }) {
  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_APPLICANTPRIVACY,
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function TrustCenter() {
  const router = useRouter();

  // Client side Apollo
  const { loading, error, data } = useQuery(GET_APPLICANTPRIVACY);

  // CSR loading
  if (loading) {
    return (
      <NextNprogress
        color="#25383F"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{
          showSpinner: false,
        }}
      />
    );
  }
  const title = data?.page?.title ?? null;
  const content = data?.page?.content ?? null;

  const meta = {
    title: data?.page?.seo.title ?? null,
    description: data?.page?.seo.metaDesc ?? null,
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <Container mt={{ base: '6', lg: '8' }}>
          <Heading
            as="h1"
            fontSize={{ base: '2.5rem', lg: '3rem' }}
            lineHeight={{ base: '2.75rem', lg: '3.5rem' }}
            mb={{ base: '4', lg: '6' }}
          >
            {title}
          </Heading>
          <Box
            className={styles.content}
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
        </Container>
      </Layout>
    </>
  );
}
